import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import toast from 'react-hot-toast';

interface BusinessCardData {
  personName: string | null;
  companyName: string | null;
  position: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
}

interface BusinessCardScannerProps {
  onCardScanned: (cardData: BusinessCardData, imageBase64: string) => void;
  onBack: () => void;
}

export const BusinessCardScanner = ({
  onCardScanned,
  onBack,
}: BusinessCardScannerProps) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  // Effect to attach stream to video element when it becomes available
  useEffect(() => {
    const attachStream = async () => {
      if (isCameraActive && videoRef.current && streamRef.current) {
        console.log('🔗 Attaching stream to video element');
        videoRef.current.srcObject = streamRef.current;

        // Wait for metadata with timeout
        console.log('⏳ Waiting for video metadata...');
        const metadataPromise = new Promise<void>((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => {
              console.log('✅ Metadata loaded');
              resolve();
            };
          } else {
            resolve();
          }
        });

        const timeoutPromise = new Promise<void>((resolve) =>
          setTimeout(() => {
            console.log('⏰ Metadata timeout - continuing anyway');
            resolve();
          }, 3000)
        );

        await Promise.race([metadataPromise, timeoutPromise]);

        // Play video
        if (videoRef.current) {
          try {
            console.log('▶️ Playing video...');
            await videoRef.current.play();
            console.log('✅ Video playing');
            toast.success('Kamera uruchomiona!');
          } catch (playError) {
            console.error('Video play error:', playError);
            toast.error('Nie można uruchomić podglądu kamery');
          }
        }
      }
    };

    attachStream();
  }, [isCameraActive]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik obrazu');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreview(result);
      // Remove data:image/jpeg;base64, prefix
      const base64 = result.split(',')[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      console.log('🎥 Starting camera...');

      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Twoja przeglądarka nie obsługuje dostępu do kamery');
        console.error('MediaDevices API not available');
        return;
      }

      // Check camera permission status (if supported)
      try {
        if ('permissions' in navigator) {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
          console.log('📋 Camera permission status:', permissionStatus.state);

          if (permissionStatus.state === 'denied') {
            toast.error(
              'Dostęp do kamery jest zablokowany. Włącz uprawnienia w ustawieniach przeglądarki: Ustawienia → Prywatność i bezpieczeństwo → Uprawnienia witryn → Kamera',
              { duration: 8000 }
            );
            return;
          }
        }
      } catch (permError) {
        console.log('⚠️ Permission API not fully supported, continuing anyway');
      }

      // Request camera access with simplified constraints for better Android compatibility
      console.log('📸 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer back camera
          // Simplified constraints - let device choose optimal resolution
        }
      });

      console.log('✅ Camera stream obtained:', stream.getVideoTracks());

      // Store stream in ref and activate camera
      streamRef.current = stream;
      setIsCameraActive(true);
      // useEffect will handle attaching stream to video element
    } catch (error: any) {
      console.error('❌ Camera error:', error);

      // Better error messages based on error type
      if (error.name === 'NotAllowedError') {
        toast.error(
          'Dostęp do kamery został odrzucony. Aby włączyć: Ustawienia przeglądarki → Uprawnienia witryn → Kamera → Zezwól',
          { duration: 8000 }
        );
      } else if (error.name === 'NotFoundError') {
        toast.error('Nie znaleziono kamery na urządzeniu');
      } else if (error.name === 'NotReadableError') {
        toast.error('Kamera jest używana przez inną aplikację');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Nie można spełnić wymagań kamery. Spróbuj ponownie.');
      } else {
        toast.error(`Nie można uruchomić kamery: ${error.message || 'nieznany błąd'}`);
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      console.log('🛑 Stopping camera');
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Business card standard aspect ratio (CR80: 85.60mm × 53.98mm)
    const BUSINESS_CARD_ASPECT = 1.586;
    const CROP_PERCENTAGE = 0.95; // Use 95% of available space to match frame

    const videoAspect = video.videoWidth / video.videoHeight;

    let cropWidth, cropHeight;

    if (videoAspect > BUSINESS_CARD_ASPECT) {
      // Video is wider than business card - constrain by height
      cropHeight = video.videoHeight * CROP_PERCENTAGE;
      cropWidth = cropHeight * BUSINESS_CARD_ASPECT;
    } else {
      // Video is taller than business card - constrain by width
      cropWidth = video.videoWidth * CROP_PERCENTAGE;
      cropHeight = cropWidth / BUSINESS_CARD_ASPECT;
    }

    // Center the crop area
    const cropX = (video.videoWidth - cropWidth) / 2;
    const cropY = (video.videoHeight - cropHeight) / 2;

    // Set canvas to cropped dimensions only
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    // Draw only the cropped business card area
    context.drawImage(
      video,
      cropX, cropY, cropWidth, cropHeight,  // Source rectangle (from video)
      0, 0, cropWidth, cropHeight           // Destination rectangle (to canvas)
    );

    const dataUrl = canvas.toDataURL('image/jpeg', 0.95); // 95% quality
    setImagePreview(dataUrl);
    const base64 = dataUrl.split(',')[1];
    setImageBase64(base64);

    stopCamera();
  };

  const handleScan = async () => {
    if (!imageBase64) {
      toast.error('Wybierz lub zrób zdjęcie wizytówki');
      return;
    }

    setIsProcessing(true);

    try {
      // Call new endpoint that doesn't require meetingId
      const response = await fetch('/api/notes/scan-card', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ imageBase64 }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Wizytówka rozpoznana!');

        // Pass both extracted data and image to parent
        onCardScanned(
          {
            personName: data.extractedData.personName,
            companyName: data.extractedData.companyName,
            position: data.extractedData.position,
            phone: data.extractedData.phone,
            email: data.extractedData.email,
            website: data.extractedData.website,
            address: data.extractedData.address,
          },
          imageBase64
        );
      } else {
        toast.error(data.message || 'Błąd podczas rozpoznawania wizytówki');
      }
    } catch (error) {
      toast.error('Błąd podczas rozpoznawania wizytówki');
      console.error('Business card scan error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Czy na pewno chcesz usunąć zdjęcie?')) {
      setImagePreview(null);
      setImageBase64(null);
      stopCamera();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Card className="p-4 sm:p-6">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Zeskanuj wizytówkę</h2>
          <span className="text-sm text-muted-foreground">OCR przez AI</span>
        </div>

        {/* Image preview or camera */}
        <div className="space-y-4">
          <div className="relative border-2 border-dashed border-border rounded-lg p-3 sm:p-4 text-center bg-card">
            {!imagePreview && !isCameraActive ? (
              <div className="space-y-4">
                <p className="text-muted-foreground">Wybierz lub zrób zdjęcie wizytówki</p>

                <div className="flex gap-2 justify-center">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                  >
                    📁 Wybierz plik
                  </Button>

                  <Button
                    onClick={startCamera}
                    variant="outline"
                  >
                    📷 Użyj kamery
                  </Button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : isCameraActive ? (
              <div className="space-y-4">
                {/* Camera preview with business card framing guide */}
                <div className="relative w-full mx-auto overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full max-h-96 mx-auto rounded-lg bg-black"
                    style={{ objectFit: 'cover' }}
                  />
                  {/* Enhanced business card framing guide - landscape rectangle */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {/* Darkened overlay outside the frame */}
                    <div className="absolute inset-0 bg-black/40" />

                    {/* Business card frame - standard CR80 aspect ratio (85.60mm × 53.98mm ≈ 1.586:1) */}
                    {/* Responsive container: full width with max constraint (padding handled by parent) */}
                    <div className="relative z-10 w-full max-w-2xl mx-auto">
                      <div
                        className="border-4 border-white rounded-lg shadow-2xl relative bg-transparent"
                        style={{
                          paddingBottom: '63%', /* 63% = 1/1.586 for business card aspect ratio */
                        }}
                      >
                        {/* Corner guides for precise alignment */}
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-400" />
                        <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-400" />
                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-400" />
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-400" />

                        {/* Instruction text - full width within padded container */}
                        <div className="absolute -top-10 inset-x-0 text-center">
                          <div className="bg-black/70 rounded py-2 px-3 text-white text-sm font-semibold inline-block max-w-full">
                            📇 Umieść wizytówkę POZIOMO w ramce
                          </div>
                        </div>

                        {/* Centering crosshair */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-full h-px bg-white/30" />
                          <div className="absolute w-px h-full bg-white/30" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 justify-center">
                  <Button onClick={capturePhoto} className="bg-green-600 hover:bg-green-700">
                    📸 Zrób zdjęcie
                  </Button>
                  <Button onClick={stopCamera} variant="outline">
                    Anuluj
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <img
                  src={imagePreview ?? undefined}
                  alt="Business card preview"
                  className="w-full max-h-96 object-contain mx-auto rounded-lg"
                />
                <Button onClick={handleClear} variant="outline" size="sm">
                  Zmień zdjęcie
                </Button>
              </div>
            )}
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={onBack} variant="outline" className="w-full sm:w-auto">
            ← Wstecz
          </Button>

          {imagePreview && !isProcessing && (
            <>
              <Button onClick={handleClear} variant="outline" className="w-full sm:w-auto">
                Wyczyść
              </Button>
              <Button onClick={handleScan} disabled={!imageBase64 || isProcessing} className="w-full sm:w-auto">
                Rozpoznaj wizytówkę
              </Button>
            </>
          )}
        </div>

        {isProcessing && (
          <div className="text-center text-sm text-muted-foreground">
            <p>Rozpoznawanie wizytówki przez AI...</p>
            <p className="text-xs mt-1">To może potrwać kilka sekund</p>
          </div>
        )}
      </div>
    </Card>
  );
};
